if (!customElements.get('video-slides-products-v2')) {
  const PREVIEW_DELAY_MS = 100;
  const SCROLL_TOLERANCE = 2;
  const FINE_POINTER_MQ = '(hover: hover) and (pointer: fine)';
  /** 仅移动端弹窗媒体最大高度 */
  const DIALOG_MOBILE_MAX_HEIGHT = 400;

  /**
   * Horizontal video-product list: overflow arrows, single active card,
   * delayed muted preview, and product-info dialog.
   */
  class VideoSlidesProductsV2 extends HTMLElement {
    connectedCallback() {
      if (this.isBound) this.unbind();

      this.viewport = this.querySelector('[data-vsp-viewport]');
      this.track = this.querySelector('[data-vsp-track]');
      this.prevBtn = this.querySelector('[data-vsp-prev]');
      this.nextBtn = this.querySelector('[data-vsp-next]');
      this.dialog = document.querySelector('#product-info-dialog');

      if (!this.viewport || !this.track) return;

      this.activeCard = null;
      this.playTimer = null;
      this.syncFrame = null;
      this.isFinePointer = window.matchMedia(FINE_POINTER_MQ).matches;
      this.arrowMq = window.matchMedia('(min-width: 768px)');

      this.onPrevClick = () => this.scrollByItem(-1);
      this.onNextClick = () => this.scrollByItem(1);
      this.onScroll = () => this.queueArrowSync();
      this.onRootClick = (event) => this.handleRootClick(event);
      this.onPointerEnter = (event) => this.handlePointerEnter(event);
      this.onPointerLeave = (event) => this.handlePointerLeave(event);
      this.onDialogClick = (event) => this.handleDialogClick(event);
      this.onDialogToggle = () => {
        if (this.dialog?.hasAttribute('open')) return;
        this.stopDialogMedia();
        this.unmarkVideoSlideDialog();
      };
      this.onMediaChange = (event) => {
        this.isFinePointer = event.matches;
      };
      this.onArrowMqChange = () => this.queueArrowSync();
      this.onDialogLayoutResize = () => this.fitMobileDialogMedia();

      this.prevBtn?.addEventListener('click', this.onPrevClick);
      this.nextBtn?.addEventListener('click', this.onNextClick);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('click', this.onRootClick);
      this.addEventListener('mouseenter', this.onPointerEnter, true);
      this.addEventListener('mouseleave', this.onPointerLeave, true);
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('toggle', this.onDialogToggle);

      this.pointerMq = window.matchMedia(FINE_POINTER_MQ);
      if (this.pointerMq.addEventListener) {
        this.pointerMq.addEventListener('change', this.onMediaChange);
      } else {
        this.pointerMq.addListener(this.onMediaChange);
      }
      if (this.arrowMq.addEventListener) {
        this.arrowMq.addEventListener('change', this.onArrowMqChange);
      } else {
        this.arrowMq.addListener(this.onArrowMqChange);
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.queueArrowSync();
          this.fitMobileDialogMedia();
        });
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.track);
      }

      window.addEventListener('resize', this.onDialogLayoutResize, { passive: true });
      window.addEventListener('orientationchange', this.onDialogLayoutResize, { passive: true });

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              this.deactivate(entry.target);
            }
          });
        },
        { root: this.viewport, threshold: 0 }
      );

      this.cards().forEach((card) => {
        this.intersectionObserver.observe(card);
        this.mergeVariantMetafields(card);
      });

      this.isBound = true;
      this.queueArrowSync();
    }

    disconnectedCallback() {
      this.unbind();
    }

    /**
     * Removes listeners, observers, and preview playback.
     */
    unbind() {
      this.clearPlayTimer();
      if (this.activeCard) this.deactivate(this.activeCard);

      this.prevBtn?.removeEventListener('click', this.onPrevClick);
      this.nextBtn?.removeEventListener('click', this.onNextClick);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('click', this.onRootClick);
      this.removeEventListener('mouseenter', this.onPointerEnter, true);
      this.removeEventListener('mouseleave', this.onPointerLeave, true);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('toggle', this.onDialogToggle);
      window.removeEventListener('resize', this.onDialogLayoutResize);
      window.removeEventListener('orientationchange', this.onDialogLayoutResize);
      this.stopDialogMedia();
      this.unmarkVideoSlideDialog();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();

      if (this.pointerMq) {
        if (this.pointerMq.removeEventListener) {
          this.pointerMq.removeEventListener('change', this.onMediaChange);
        } else {
          this.pointerMq.removeListener(this.onMediaChange);
        }
      }
      if (this.arrowMq) {
        if (this.arrowMq.removeEventListener) {
          this.arrowMq.removeEventListener('change', this.onArrowMqChange);
        } else {
          this.arrowMq.removeListener(this.onArrowMqChange);
        }
      }

      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.isBound = false;
    }

    /**
     * @returns {HTMLElement[]}
     */
    cards() {
      return Array.from(this.track.querySelectorAll('[data-video-slide-product]'));
    }

    /**
     * Coalesces scroll/resize into one arrow visibility pass.
     */
    queueArrowSync() {
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.syncArrows();
      });
    }

    /**
     * Shows prev/next only on desktop when that direction can still scroll.
     */
    syncArrows() {
      if (!this.prevBtn || !this.nextBtn) return;

      if (!this.arrowMq?.matches) {
        this.prevBtn.hidden = true;
        this.nextBtn.hidden = true;
        return;
      }

      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const canScroll = maxScroll > SCROLL_TOLERANCE;
      if (!canScroll) {
        this.prevBtn.hidden = true;
        this.nextBtn.hidden = true;
        return;
      }

      this.prevBtn.hidden = this.viewport.scrollLeft <= SCROLL_TOLERANCE;
      this.nextBtn.hidden = this.viewport.scrollLeft >= maxScroll - SCROLL_TOLERANCE;
    }

    /**
     * @param {-1 | 1} direction
     */
    scrollByItem(direction) {
      const firstItem = Array.from(this.track.children).find((item) => {
        return !item.hidden && getComputedStyle(item).display !== 'none';
      });
      const itemWidth = firstItem?.getBoundingClientRect().width || 0;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const step = itemWidth + gap;
      if (step <= 0) return;

      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const targetLeft = Math.min(maxScroll, Math.max(0, this.viewport.scrollLeft + direction * step));
      this.viewport.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }

    /**
     * @param {MouseEvent} event
     */
    handlePointerEnter(event) {
      if (!this.isFinePointer) return;
      const card = event.target.closest?.('[data-video-slide-product]');
      if (!card || !this.track.contains(card)) return;
      this.activate(card);
    }

    /**
     * @param {MouseEvent} event
     */
    handlePointerLeave(event) {
      if (!this.isFinePointer) return;
      const card = event.target.closest?.('[data-video-slide-product]');
      if (!card || !this.track.contains(card)) return;
      const next = event.relatedTarget;
      if (next instanceof Node && card.contains(next)) return;
      this.deactivate(card);
    }

    /**
     * @param {MouseEvent} event
     */
    handleRootClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // 产品胶囊：保留跳转，不抢开弹窗
      const capsule = target.closest('[data-vsp-capsule]');
      if (capsule) {
        event.stopPropagation();
        sessionStorage.setItem('fromModule', 'UVideo');
        return;
      }

      const card = target.closest('[data-video-slide-product]');
      if (!card || !this.track.contains(card)) return;
      if (card.classList.contains('is-loading')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const hasPreview = card.dataset.hasPreviewVideo === 'true';
      const isPreviewEngaged =
        card.classList.contains('is-playing') || card.classList.contains('is-active');

      /*
        预览已激活/播放中：除胶囊外整卡可开弹窗（封面、按钮、下方文案等）。
        无预览视频时，任意点击直接开弹窗。
        列表预览在弹窗确认打开后再停并回封面（见 showDialog）。
      */
      if (!hasPreview || isPreviewEngaged) {
        event.preventDefault();
        event.stopPropagation();
        this.showDialog(card);
        return;
      }

      const trigger = target.closest('[data-vsp-dialog-trigger]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        this.activate(card);
        return;
      }

      if (this.isFinePointer) return;

      this.activate(card);
    }

    /**
     * @param {HTMLElement} card
     */
    activate(card) {
      if (!card || this.activeCard === card) return;
      if (this.activeCard) this.deactivate(this.activeCard);

      this.activeCard = card;
      card.classList.add('is-active');
      this.clearPlayTimer();

      const video = card.querySelector('.video-slide-product__media video');
      if (!video) return;

      this.prepareCardPreviewVideo(video);
      this.revealCardPreviewVideo(video);

      const startPreview = () => {
        if (this.activeCard !== card) return;
        video.muted = true;
        video.playsInline = true;
        const playResult = video.play();
        if (playResult && typeof playResult.then === 'function') {
          playResult
            .then(() => {
              if (this.activeCard === card) {
                card.classList.add('is-playing');
                this.exitNativeVideoFullscreen(video);
              }
            })
            .catch(() => {});
        } else {
          card.classList.add('is-playing');
          this.exitNativeVideoFullscreen(video);
        }
      };

      // 粗指针（手机）：play 须在手势同步栈；细指针（PC hover）可短暂延迟
      if (!this.isFinePointer) {
        startPreview();
      } else {
        this.playTimer = setTimeout(startPreview, PREVIEW_DELAY_MS);
      }
    }

    /**
     * @param {HTMLElement | null} card
     */
    deactivate(card) {
      if (!card) return;
      if (this.activeCard === card) this.activeCard = null;
      this.clearPlayTimer();
      card.classList.remove('is-active', 'is-playing');
      const video = card.querySelector('.video-slide-product__media video');
      if (video) this.releaseCardPreviewVideo(video);
    }

    clearPlayTimer() {
      if (this.playTimer) {
        clearTimeout(this.playTimer);
        this.playTimer = null;
      }
    }

    /**
     * 卡片预览：尽量内联播放，并拦截进入全屏的默认播放器。
     * @param {HTMLVideoElement} video
     */
    prepareCardPreviewVideo(video) {
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('x5-playsinline', 'true');
      video.setAttribute('x5-video-player-type', 'h5');
      video.setAttribute('x5-video-player-fullscreen', 'false');
      video.setAttribute('disablepictureinpicture', '');
      video.playsInline = true;
      video.muted = true;
      video.defaultMuted = true;
      video.controls = false;
      try {
        video.disablePictureInPicture = true;
      } catch (error) {
        /* unsupported */
      }

      if (video.dataset.vspFsGuard === 'true') return;
      video.dataset.vspFsGuard = 'true';
      const onFs = () => this.exitNativeVideoFullscreen(video);
      video.addEventListener('webkitbeginfullscreen', onFs);
      video.addEventListener('webkitfullscreenchange', onFs);
      video.addEventListener('fullscreenchange', onFs);
    }

    /**
     * 恢复可播放的卡片 video（去掉停播时的强制隐藏）。
     * @param {HTMLVideoElement} video
     */
    revealCardPreviewVideo(video) {
      video.style.removeProperty('display');
      video.style.removeProperty('visibility');
      video.style.removeProperty('opacity');
    }

    /**
     * 停播并拆掉安卓默认播放器残留合成层，露出封面。
     * 仅 pause + opacity 不够：硬件层常无视 opacity。
     * @param {HTMLVideoElement} video
     */
    releaseCardPreviewVideo(video) {
      video.pause();
      try {
        video.currentTime = 0;
      } catch (error) {
        /* ignore unseekable preview */
      }
      this.exitNativeVideoFullscreen(video);
      // display:none 比 visibility/opacity 更能拆掉 Android video 合成层
      video.style.opacity = '0';
      video.style.visibility = 'hidden';
      video.style.display = 'none';
      try {
        video.load();
      } catch (error) {
        /* ignore */
      }
    }

    /**
     * 若仍进入全屏/系统播放器，尝试立即退出，回到卡片内画面。
     * @param {HTMLVideoElement} video
     */
    exitNativeVideoFullscreen(video) {
      try {
        if (video.webkitDisplayingFullscreen && typeof video.webkitExitFullscreen === 'function') {
          video.webkitExitFullscreen();
        } else if (typeof video.webkitExitFullscreen === 'function') {
          video.webkitExitFullscreen();
        }
      } catch (error) {
        /* ignore */
      }
      try {
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      } catch (error) {
        /* ignore */
      }
    }

    /**
     * @param {HTMLElement} card
     */
    mergeVariantMetafields(card) {
      const node = card.querySelector('[data-vsp-variant-metafields]');
      if (!node) return;
      try {
        const payload = JSON.parse(node.textContent || '{}');
        if (!payload.productId || !payload.variants) return;
        window.variantMetafields = window.variantMetafields || {};
        window.variantMetafields[payload.productId] = payload.variants;
      } catch (error) {
        /* ignore malformed metafield JSON */
      }
    }

    /**
     * @param {string} url
     * @returns {boolean}
     */
    isYoutubeUrl(url) {
      if (!url) return false;
      try {
        const host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
        return (
          host === 'youtube.com' ||
          host === 'm.youtube.com' ||
          host === 'youtube-nocookie.com' ||
          host === 'youtu.be'
        );
      } catch (error) {
        return /youtube\.com|youtu\.be/i.test(url);
      }
    }

    /**
     * 弹窗内左右键：切到相邻 card 并刷新弹窗内容
     * @param {MouseEvent} event
     */
    handleDialogClick(event) {
      const target = event.target;
      if (!(target instanceof Element) || !this.dialog) return;

      const icon = target.closest('.video-slides-product-icon');
      if (!icon || !this.dialog.contains(icon)) return;

      const nextBlockId = icon.getAttribute('data-block-id');
      if (!nextBlockId || !this.track) return;

      event.preventDefault();
      event.stopPropagation();

      const nextCard = this.cards().find(
        (card) => card.getAttribute('data-block-id') === nextBlockId
      );
      if (nextCard) this.showDialog(nextCard);
    }

    /**
     * 停止弹窗内所有媒体，避免关闭后继续出声
     */
    stopDialogMedia() {
      if (!this.dialog) return;

      this.clearMobileDialogMediaFit();

      this.dialog.querySelectorAll('video-media').forEach((media) => {
        if (typeof media.pause === 'function') media.pause();
      });

      this.dialog.querySelectorAll('video').forEach((video) => {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (error) {
          // ignore seek errors on unloaded media
        }
      });

      this.dialog.querySelectorAll('iframe').forEach((iframe) => {
        iframe.src = '';
      });
    }

    /**
     * 读取弹窗 video-media 上的 --aspect-ratio（Shopify 展示比）
     * @param {HTMLElement} el
     * @returns {number}
     */
    readAspectRatio(el) {
      if (!el) return 0;
      const fromStyle = Number.parseFloat(
        (el.getAttribute('style') || '').match(/--aspect-ratio:\s*([\d.]+)/)?.[1] || ''
      );
      if (fromStyle > 0) return fromStyle;
      const computed = Number.parseFloat(
        getComputedStyle(el).getPropertyValue('--aspect-ratio') || ''
      );
      return computed > 0 ? computed : 0;
    }

    /**
     * 移动端弹窗媒体：像素级 contain（避开 iOS aspect-ratio + max-height 撑高黑边）。
     * PC 直接 return，不影响已调好的桌面表现。
     */
    fitMobileDialogMedia() {
      if (!this.dialog?.hasAttribute('open')) return;
      if (window.matchMedia('(min-width: 768px)').matches) return;

      const wrapper = this.dialog.querySelector('.product-media-wrapper');
      const media = this.dialog.querySelector('.video-slide-product__dialog-media');
      if (!wrapper || !media) return;

      const isPortrait = media.classList.contains(
        'video-slide-product__dialog-media--portrait'
      );
      const isLandscape = media.classList.contains(
        'video-slide-product__dialog-media--landscape'
      );
      const videoMedia = media.querySelector('video-media');
      const youtubeWrap = media.classList.contains('video-slides--youtube-video-wrapper')
        ? media
        : null;

      const containerW = wrapper.clientWidth;
      if (containerW <= 0) return;

      wrapper.style.maxHeight = `${DIALOG_MOBILE_MAX_HEIGHT}px`;
      wrapper.style.minHeight = '0';
      wrapper.style.overflow = 'hidden';
      wrapper.style.justifyContent = 'center';
      media.style.maxHeight = `${DIALOG_MOBILE_MAX_HEIGHT}px`;
      media.style.minHeight = '0';
      media.style.overflow = 'hidden';
      media.style.width = '100%';

      if (youtubeWrap) {
        const h = Math.min(DIALOG_MOBILE_MAX_HEIGHT, (containerW * 9) / 16);
        youtubeWrap.style.width = '100%';
        youtubeWrap.style.height = `${h}px`;
        youtubeWrap.style.maxHeight = `${DIALOG_MOBILE_MAX_HEIGHT}px`;
        wrapper.style.height = `${h}px`;
        return;
      }

      if (!videoMedia) return;
      const ratio = this.readAspectRatio(videoMedia);
      if (!(ratio > 0)) return;

      let renderW;
      let renderH;

      if (isLandscape) {
        // 宽度占满，高度按比例（通常远小于 400）
        renderW = containerW;
        renderH = Math.min(DIALOG_MOBILE_MAX_HEIGHT, containerW / ratio);
      } else if (isPortrait) {
        // 触顶：高度=400，宽度按比例收窄；未触顶：宽占满，高按比例
        const naturalH = containerW / ratio;
        if (naturalH > DIALOG_MOBILE_MAX_HEIGHT) {
          renderH = DIALOG_MOBILE_MAX_HEIGHT;
          renderW = DIALOG_MOBILE_MAX_HEIGHT * ratio;
        } else {
          renderW = containerW;
          renderH = naturalH;
        }
      } else {
        return;
      }

      videoMedia.style.boxSizing = 'border-box';
      videoMedia.style.width = `${renderW}px`;
      videoMedia.style.height = `${renderH}px`;
      videoMedia.style.maxWidth = '100%';
      videoMedia.style.maxHeight = `${DIALOG_MOBILE_MAX_HEIGHT}px`;
      videoMedia.style.marginInline = 'auto';
      videoMedia.style.overflow = 'hidden';

      const nativeVideo = videoMedia.querySelector('video');
      if (nativeVideo) {
        nativeVideo.style.width = '100%';
        nativeVideo.style.height = '100%';
        nativeVideo.style.objectFit = 'contain';
        nativeVideo.style.objectPosition = 'center';
      }

      media.style.height = `${renderH}px`;
      wrapper.style.height = `${renderH}px`;
    }

    /**
     * 清除移动端弹窗媒体上的内联尺寸，避免污染下次打开 / PC
     */
    clearMobileDialogMediaFit() {
      if (!this.dialog) return;

      const wrapper = this.dialog.querySelector('.product-media-wrapper');
      if (wrapper) {
        wrapper.style.maxHeight = '';
        wrapper.style.minHeight = '';
        wrapper.style.height = '';
        wrapper.style.overflow = '';
        wrapper.style.justifyContent = '';
      }

      this.dialog
        .querySelectorAll('.video-slide-product__dialog-media')
        .forEach((media) => {
          media.style.maxHeight = '';
          media.style.minHeight = '';
          media.style.height = '';
          media.style.width = '';
          media.style.overflow = '';
        });

      this.dialog.querySelectorAll('video-media.video-slides--video').forEach((el) => {
        el.style.boxSizing = '';
        el.style.width = '';
        el.style.height = '';
        el.style.maxWidth = '';
        el.style.maxHeight = '';
        el.style.marginInline = '';
        el.style.overflow = '';
      });

      this.dialog
        .querySelectorAll('.video-slide-product__dialog-media video')
        .forEach((video) => {
          video.style.width = '';
          video.style.height = '';
          video.style.objectFit = '';
          video.style.objectPosition = '';
        });
    }

    /**
     * @param {string} url
     * @returns {string | null}
     */
    toYoutubeEmbedUrl(url) {
      if (!this.isYoutubeUrl(url)) return null;

      let videoId = '';
      try {
        const parsed = new URL(url);
        const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
        if (host === 'youtu.be') {
          videoId = parsed.pathname.split('/').filter(Boolean)[0] || '';
        } else if (parsed.pathname.includes('/embed/')) {
          videoId = parsed.pathname.split('/embed/')[1]?.split('/')[0] || '';
        } else if (parsed.pathname.includes('/shorts/')) {
          videoId = parsed.pathname.split('/shorts/')[1]?.split('/')[0] || '';
        } else {
          videoId = parsed.searchParams.get('v') || '';
        }
      } catch (error) {
        if (url.includes('watch?v=')) {
          videoId = url.split('watch?v=')[1].split('&')[0];
        }
      }

      if (!videoId) return null;
      return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1`;
    }

    /**
     * Stacks full author/content directly under the dialog video in one column,
     * so PC product-info height cannot push the copy down.
     * @param {HTMLElement} card
     * @param {HTMLElement} productSection
     */
    insertDialogCopy(card, productSection) {
      const product = productSection.querySelector('.product');
      const media = product?.querySelector('.product-media-wrapper');
      if (!product || !media) return;

      const sourceCopy = card.querySelector('.video-slide-product__copy');
      if (!sourceCopy) return;

      const mediaCol = document.createElement('div');
      mediaCol.className = 'video-slide-product__dialog-media-col';
      media.replaceWith(mediaCol);
      mediaCol.appendChild(media);

      const dialogCopy = document.createElement('div');
      dialogCopy.className = 'video-slide-product__dialog-copy';
      dialogCopy.innerHTML = sourceCopy.innerHTML;
      mediaCol.appendChild(dialogCopy);

      product.classList.add('product--video-slide-copy');
    }

    /**
     * Marks the shared dialog so v2 size CSS does not leak to other openers.
     */
    markVideoSlideDialog() {
      this.dialog?.setAttribute('data-vsp-dialog', '');
    }

    /**
     * Clears the v2 size marker when this section closes or unloads the dialog.
     */
    unmarkVideoSlideDialog() {
      this.dialog?.removeAttribute('data-vsp-dialog');
    }

    /**
     * Opens #product-info-dialog with product HTML and this card's dialog media.
     * 加载期间保持列表预览；确认可打开弹窗后再停预览并回封面，避免加载等待时闪封面。
     * @param {HTMLElement} card
     */
    async showDialog(card) {
      const handle = card.getAttribute('data-product-handle');
      const blockId = card.getAttribute('data-block-id');
      if (!handle || !this.dialog) return;
      if (card.classList.contains('is-loading')) return;

      const dialogInfo = this.dialog.querySelector('.dialog-body-box');
      if (!dialogInfo) return;

      // 切换 item 前先停掉当前弹窗媒体，避免切换/加载间隙继续出声
      this.stopDialogMedia();

      card.classList.add('is-loading');

      try {
        const url = `${Shopify.routes.root}products/${handle}`;
        const response = await fetch(url, { priority: 'low' });
        const tempDiv = new DOMParser().parseFromString(await response.text(), 'text/html');
        const productInfoElm = tempDiv.querySelector('#main .shopify-section--main-product');
        if (!productInfoElm) return;

        const addToCartBarWrapper = productInfoElm.querySelector('.add-to-cart-bar-wrapper');
        const quickBuyContent = productInfoElm.querySelector('#quickBuyContent');
        if (addToCartBarWrapper) addToCartBarWrapper.parentNode.removeChild(addToCartBarWrapper);
        if (quickBuyContent) productInfoElm.removeChild(quickBuyContent);

        const buyButtonEle = productInfoElm.querySelector('.buy-buttons');
        if (buyButtonEle) {
          const gtaName = buyButtonEle.getAttribute('data-gta');
          if (gtaName) buyButtonEle.setAttribute('data-gta', `UVD-${gtaName}`);
        }

        const sourceMedia = card.querySelector('.video-slide-product__dialog-media');
        const productMedia = productInfoElm.querySelector('.product-media-wrapper');
        const hasDialogMedia =
          sourceMedia &&
          (sourceMedia.dataset.youtubeUrl || sourceMedia.querySelector('video, iframe, video-media'));
        if (hasDialogMedia && productMedia) {
          const clone = sourceMedia.cloneNode(true);
          clone.hidden = false;
          clone.removeAttribute('hidden');

          const cards = this.cards();
          const index = cards.indexOf(card);
          const prevCard = index > 0 ? cards[index - 1] : null;
          const nextCard = index < cards.length - 1 ? cards[index + 1] : null;
          const prevIcon = clone.querySelector('.video-slides-prev-product-icon');
          const nextIcon = clone.querySelector('.video-slides-next-product-icon');

          if (prevCard && prevIcon) {
            prevIcon.setAttribute('data-block-id', prevCard.getAttribute('data-block-id') || '');
            prevIcon.setAttribute('data-product-handle', prevCard.getAttribute('data-product-handle') || '');
          } else if (prevIcon) {
            prevIcon.remove();
          }

          if (nextCard && nextIcon) {
            nextIcon.setAttribute('data-block-id', nextCard.getAttribute('data-block-id') || '');
            nextIcon.setAttribute('data-product-handle', nextCard.getAttribute('data-product-handle') || '');
          } else if (nextIcon) {
            nextIcon.remove();
          }

          productMedia.innerHTML = clone.outerHTML;

          const dialogVideoWrapper = productInfoElm.querySelector('.video-slide-product__dialog-media');
          const youtubeUrl = dialogVideoWrapper?.dataset.youtubeUrl || '';
          const embedUrl = this.toYoutubeEmbedUrl(youtubeUrl);
          if (embedUrl) {
            const youtubeIframe = dialogVideoWrapper.querySelector('.youtube-embed');
            if (youtubeIframe) youtubeIframe.src = embedUrl;
          }
        }

        this.insertDialogCopy(card, productInfoElm);

        dialogInfo.innerHTML = productInfoElm.outerHTML;
        // Scopes the dialog rules in video-slides-products-v2.css; x-popover removes the attribute on close.
        this.dialog.setAttribute('data-layout', 'video-slides-v2');
        this.markVideoSlideDialog();
        this.dialog.setAttribute('open', '');

        // 弹窗已确认打开后再停列表预览 / 默认播放器并回封面（加载等待期间继续播预览）
        this.deactivate(card);
        if (this.activeCard && this.activeCard !== card) {
          this.deactivate(this.activeCard);
        }

        // dialog 刚插入时 IntersectionObserver(inView) 可能尚未触发，主动调 video-media.play()
        const videoMedia = this.dialog.querySelector(
          '.video-slide-product__dialog-media video-media'
        );
        if (videoMedia && typeof videoMedia.play === 'function') {
          videoMedia.play();
        } else {
          const nativeVideo = this.dialog.querySelector(
            '.video-slide-product__dialog-media video'
          );
          if (nativeVideo) {
            const playResult = nativeVideo.play();
            if (playResult && typeof playResult.catch === 'function') {
              playResult.catch(() => {});
            }
          }
        }

        // 等布局完成后再算移动端弹窗尺寸（竖屏触顶收窄，避免 iOS 额外黑边）
        requestAnimationFrame(() => {
          this.fitMobileDialogMedia();
          requestAnimationFrame(() => this.fitMobileDialogMedia());
        });
      } finally {
        card.classList.remove('is-loading');
      }
    }
  }

  customElements.define('video-slides-products-v2', VideoSlidesProductsV2);
}
